/**
 * CreateProjectPage - form for creating a new project
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clsx } from 'clsx';
import { ArrowLeft } from 'lucide-react';
import { useProjectStore } from '@stores/projectStore';
import { useAuthStore } from '@stores/authStore';
import {
  IconPicker,
  ColorPicker,
  colorOptions,
} from '@/components/shared/IconPicker';

const projectSchema = z.object({
  name: z
    .string()
    .min(1, 'Project name is required')
    .max(100, 'Project name must be less than 100 characters'),
  key: z
    .string()
    .min(2, 'Key must be at least 2 characters')
    .max(10, 'Key must be less than 10 characters')
    .transform((val) => val.toUpperCase())
    .refine(
      (val) => /^[A-Z][A-Z0-9]*$/.test(val),
      'Key must start with a letter and contain only uppercase letters and numbers'
    ),
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
  isPublic: z.boolean().default(false),
});

type ProjectFormData = z.infer<typeof projectSchema>;

export default function CreateProjectPage() {
  const navigate = useNavigate();
  // t available for future i18n

  const { t: _t } = useTranslation();
  const { currentOrgId } = useAuthStore();
  const { createProject, isLoading, error } = useProjectStore();

  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [selectedIcon, setSelectedIcon] = useState<string>('folder-kanban');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      key: '',
      description: '',
      isPublic: false,
    },
  });

  const projectName = watch('name');

  // Auto-generate key from name
  const generateKey = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 10);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue('name', name);

    // Auto-generate key if user hasn't manually edited it
    const currentKey = watch('key');
    const generatedKey = generateKey(projectName);
    if (!currentKey || currentKey === generatedKey) {
      setValue('key', generateKey(name));
    }
  };

  const onSubmit = async (data: ProjectFormData) => {
    if (!currentOrgId) return;

    try {
      const createData: {
        organizationId: string;
        name: string;
        key: string;
        isPublic: boolean;
        description?: string;
        color?: string;
        icon?: string;
      } = {
        organizationId: currentOrgId,
        name: data.name,
        key: data.key,
        isPublic: data.isPublic,
      };
      if (data.description) {
        createData.description = data.description;
      }
      if (selectedColor) {
        createData.color = selectedColor;
      }
      if (selectedIcon) {
        createData.icon = selectedIcon;
      }
      const project = await createProject(createData);
      navigate(`/projects/${project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white px-6 py-4 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto max-w-2xl">
          <button
            onClick={() => navigate('/projects')}
            className={clsx(
              'flex items-center gap-2 text-sm',
              'text-neutral-500 hover:text-neutral-700',
              'dark:text-neutral-400 dark:hover:text-neutral-200'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Projects
          </button>
          <h1 className="mt-4 text-2xl font-bold text-neutral-900 dark:text-white">
            Create New Project
          </h1>
          <p className="mt-1 text-neutral-500 dark:text-neutral-400">
            Set up a new project for your team
          </p>
        </div>
      </header>

      {/* Form */}
      <main className="mx-auto max-w-2xl px-6 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Icon Picker */}
          <div>
            <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Project Icon
            </p>
            <IconPicker
              value={selectedIcon}
              onChange={setSelectedIcon}
              color={selectedColor}
            />
          </div>

          {/* Color Picker */}
          <div>
            <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Icon Color
            </p>
            <ColorPicker value={selectedColor} onChange={setSelectedColor} />
          </div>

          {/* Project name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Project Name *
            </label>
            <input
              {...register('name')}
              type="text"
              onChange={handleNameChange}
              className={clsx(
                'mt-1 w-full rounded-lg border px-4 py-2.5',
                'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                errors.name
                  ? 'border-red-500'
                  : 'border-neutral-300 dark:border-neutral-700',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
              )}
              placeholder="e.g., Marketing Website"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          {/* Project key */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Project Key *
            </label>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Used as a prefix for task IDs (e.g., MKTG-1, MKTG-2)
            </p>
            <input
              {...register('key')}
              type="text"
              className={clsx(
                'mt-1 w-full rounded-lg border px-4 py-2.5 uppercase',
                'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                errors.key
                  ? 'border-red-500'
                  : 'border-neutral-300 dark:border-neutral-700',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
              )}
              placeholder="e.g., MKTG"
            />
            {errors.key && (
              <p className="mt-1 text-sm text-red-500">{errors.key.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className={clsx(
                'mt-1 w-full rounded-lg border px-4 py-2.5',
                'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                'border-neutral-300 dark:border-neutral-700',
                'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
              )}
              placeholder="What's this project about?"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          {/* Visibility */}
          <div>
            <label className="flex items-center gap-3">
              <input
                {...register('isPublic')}
                type="checkbox"
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Public Project
                </span>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  All organization members can view this project
                </p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-neutral-200 pt-6 dark:border-neutral-800">
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className={clsx(
                'rounded-lg px-4 py-2.5 text-sm font-medium',
                'text-neutral-700 hover:bg-neutral-100',
                'dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={clsx(
                'rounded-lg px-4 py-2.5 text-sm font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isLoading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
